import { ActionFieldsFragment_ } from 'models';
import { EsrsReportStandard } from '../../Report.types';
import { addEmptySpace, sanitizeText } from '../report-helper-functions';
import { getReportMDRA } from '../../ReportUtils';
import { uniqBy } from 'lodash';
import { Paragraph } from 'docx';
import { TextStyle } from '../reportThemes';
import { createMDR, createTargetActionQuestion } from './mdr';

export const createActions = (
  actions: ActionFieldsFragment_[] | undefined,
  actionQuestions: EsrsReportStandard['actionQuestions'],
  companyCurrency: string
) => {
  if (!actions?.length) return [];

  const displayedActions = actions.flatMap((action) => {
    const actionSection = createAction(action, actionQuestions, companyCurrency);
    return [actionSection, addEmptySpace(200)];
  });

  return displayedActions.flat();
};

export const createAction = (
  action: ActionFieldsFragment_,
  actionQuestions: EsrsReportStandard['actionQuestions'],
  companyCurrency: string
) => {
  const actionsMDR = getReportMDRA();

  const groupActionQuestions = uniqBy(
    actionQuestions.map((aq) => aq.group)?.filter((aq) => !!aq) ?? [],
    'reference'
  );
  const groupedActionQuestions = groupActionQuestions.map((group) => ({
    ...group,
    actionQuestions: actionQuestions.filter((q) => q.groupRef === group?.reference) ?? [],
  }));

  const singleActionQuestions = actionQuestions.filter((tq) => tq.groupRef === null);

  const title = sanitizeText(action.title);

  const titleParagraph = new Paragraph({
    text: title,
    style: TextStyle.h3,
    spacing: {
      after: 300,
    },
  });

  const titleMDRA = new Paragraph({
    text: 'MDR-A',
    style: TextStyle.h4,
    spacing: {
      after: 200,
    },
  });

  const titleOtherDisclosures = new Paragraph({
    text: 'Other Disclosures',
    style: TextStyle.h4,
    spacing: {
      after: 200,
    },
  });

  const displayedMDRA = actionsMDR.flatMap((mdr) => {
    const mdrtSection = createMDR({ mdr, action });
    return [...mdrtSection, addEmptySpace(200)];
  });

  const displayedActionQuestionGroup = groupedActionQuestions.flatMap((group) => {
    const groupQuestionTitle = new Paragraph({
      text: group.title,
      style: TextStyle.h4,
      spacing: {
        after: 150,
      },
    });
    const groupActionQuestionsSection = group.actionQuestions?.flatMap((question) => {
      const actionQuestionSection = createTargetActionQuestion(
        question,
        action.answers,
        companyCurrency
      );
      return [...actionQuestionSection, addEmptySpace(100)];
    });
    return [groupQuestionTitle, ...groupActionQuestionsSection, addEmptySpace(100)];
  });

  const displayedSingleActionQuestion = singleActionQuestions.flatMap((question) => {
    const actionQuestionSection = createTargetActionQuestion(
      question,
      action.answers,
      companyCurrency
    );
    return [...actionQuestionSection, addEmptySpace(100)];
  });

  return [
    titleParagraph,
    titleMDRA,
    ...displayedMDRA,
    titleOtherDisclosures,
    ...displayedActionQuestionGroup,
    ...displayedSingleActionQuestion,
  ];
};
